const clamp255 = (n) => Math.max(0, Math.min(255, Math.round(n)));

/** Default checkout theme when `customization.checkout_styles` is absent or empty. */
export const CHECKOUT_DEFAULT_STYLES = {
  primary: {
    backgroundColor: "#21ba45",
    borderColor: "#44bd60",
    color: "#fff",
    hoverBgColor: "#16ab39",
    hoverBorderColor: "#44bd60",
    hoverColor: "#fff",
  },
  link: {
    linkColor: "#1890ff",
    hoverLinkColor: "#40a9ff",
  },
  input: {
    borderColor: "#d9d9d9",
    hoverBorderColor: "#40a9ff",
    focusBorderColor: "#40a9ff",
  },
};

export function hexToRgb(hex) {
  if (hex == null || typeof hex !== "string") return null;
  let h = hex.trim();
  if (!h.startsWith("#")) return null;
  h = h.slice(1);
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r, g, b) {
  return "#" + [r, g, b].map((x) => ((1 << 8) | clamp255(x)).toString(16).slice(1)).join("");
}

function cloneDeepStyles(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/** @param {string} section primary|link|input @param {string} key @param {unknown} raw */
function normalizeMergedColor(section, key, raw) {
  if (raw == null || typeof raw !== "string") return null;
  const s = raw.trim();
  if (s === "") return null;
  const withHash = s.startsWith("#") ? s : `#${s}`;
  const rgb = hexToRgb(withHash);
  if (!rgb) return null;
  return rgbToHex(rgb.r, rgb.g, rgb.b).toLowerCase();
}

/**
 * Deep-merge producer overrides onto defaults. Outline buttons derive from `primary` in CustomButton (not stored).
 * @param {object} partialStyles - customization.checkout_styles
 * @param {object} defaults - same shape as CHECKOUT_DEFAULT_STYLES
 */
export function mergeCheckoutStyles(partialStyles, defaults) {
  const out = cloneDeepStyles(defaults);
  if (!partialStyles || typeof partialStyles !== "object") return out;
  for (const section of ["primary", "link", "input"]) {
    const src = partialStyles[section];
    if (!src || typeof src !== "object" || !out[section]) continue;
    for (const key of Object.keys(src)) {
      if (!Object.prototype.hasOwnProperty.call(out[section], key)) continue;
      const norm = normalizeMergedColor(section, key, src[key]);
      if (norm != null) out[section][key] = norm;
    }
  }

  // Input hover/focus must follow the active primary palette. If the producer only sets `input.borderColor`
  // (or nothing), defaults would keep SalesPark green — wrong for Ruby, Violet, etc.
  const bg = out.primary?.backgroundColor;
  const bd = out.primary?.borderColor;
  if (bg && bd) {
    const inPart = partialStyles.input;
    const explicitHover =
      inPart &&
      typeof inPart === "object" &&
      inPart.hoverBorderColor != null &&
      String(inPart.hoverBorderColor).trim() !== "" &&
      normalizeMergedColor("input", "hoverBorderColor", inPart.hoverBorderColor) != null;
    const explicitFocus =
      inPart &&
      typeof inPart === "object" &&
      inPart.focusBorderColor != null &&
      String(inPart.focusBorderColor).trim() !== "" &&
      normalizeMergedColor("input", "focusBorderColor", inPart.focusBorderColor) != null;
    if (!explicitHover) out.input.hoverBorderColor = bd;
    if (!explicitFocus) out.input.focusBorderColor = bg;
  }

  return out;
}

/**
 * Non-empty `checkout_styles` merged onto defaults; otherwise defaults only.
 * @param {object} [customization] - advanced.customization
 * @param {object} defaults - CHECKOUT_DEFAULT_STYLES
 */
export function resolveCheckoutTheme(customization, defaults) {
  const base = cloneDeepStyles(defaults);
  const cs = customization?.checkout_styles;
  if (cs != null && typeof cs === "object" && Object.keys(cs).length > 0) {
    return mergeCheckoutStyles(cs, base);
  }
  return base;
}

/**
 * Scoped CSS: Ant Design 4 checkboxes use compiled @primary-color (blue). Map checked/hover/focus to checkout `primary.backgroundColor`.
 * @param {string} scopeSelector - ancestor of checkboxes, e.g. `.main-container-card`
 * @param {object} customStyle - merged theme from resolveCheckoutTheme
 * @returns {string} CSS or empty if no primary color
 */
export function checkoutPrimaryCheckboxOverrideCss(scopeSelector, customStyle) {
  const primary = customStyle?.primary?.backgroundColor;
  if (primary == null || typeof primary !== "string") return "";
  const p = primary.trim();
  if (p === "") return "";
  return `
${scopeSelector} .ant-checkbox:hover .ant-checkbox-inner,
${scopeSelector} .ant-checkbox-wrapper:hover .ant-checkbox-inner,
${scopeSelector} .ant-checkbox-input:focus + .ant-checkbox-inner {
  border-color: ${p} !important;
}
${scopeSelector} .ant-checkbox-checked .ant-checkbox-inner {
  background-color: ${p} !important;
  border-color: ${p} !important;
}
${scopeSelector} .ant-checkbox-checked .ant-checkbox-inner::after {
  border-color: #fff !important;
}
${scopeSelector} .ant-checkbox-checked::after {
  border-color: ${p} !important;
}
${scopeSelector} .ant-checkbox-indeterminate .ant-checkbox-inner {
  background-color: ${p} !important;
  border-color: ${p} !important;
}
`.trim();
}

/**
 * Scoped overrides for Input, affix wrapper, Select selector, DatePicker, and react-international-phone — antd / RIP use non-theme blues.
 * Colors come from checkout_styles.input (producer-configured).
 */
export function checkoutFormControlsOverrideCss(scopeSelector, customStyle) {
  const inp = customStyle?.input;
  if (!inp || typeof inp !== "object") return "";

  const border = inp.borderColor?.trim();
  const hover = inp.hoverBorderColor?.trim();
  const focus = inp.focusBorderColor?.trim();
  if (!border && !hover && !focus) return "";

  const restBorder = border || "#d9d9d9";
  const hoverBorder = hover || focus;
  const focusBorder = focus || hover;
  const focusRgb = focusBorder ? hexToRgb(focusBorder) : null;
  const shadow = focusRgb != null ? `0 0 0 2px rgba(${focusRgb.r}, ${focusRgb.g}, ${focusRgb.b}, 0.2)` : "none";

  const s = scopeSelector;
  const rules = [];

  rules.push(`${s} .ant-input:not(:disabled):not([disabled]) { border-color: ${restBorder} !important; }`);
  rules.push(`${s} .ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled) { border-color: ${restBorder} !important; }`);
  rules.push(`${s} .ant-select:not(.ant-select-disabled):not(.ant-select-customize-input) .ant-select-selector { border-color: ${restBorder} !important; }`);
  rules.push(`${s} .ant-picker:not(.ant-picker-disabled) { border-color: ${restBorder} !important; }`);

  rules.push(
    `${s} .react-international-phone-input-container .react-international-phone-input:not(:disabled):not([disabled]) { border-color: ${restBorder} !important; }`,
  );
  rules.push(
    `${s} .react-international-phone-country-selector-button:not(.react-international-phone-country-selector-button--disabled) { border-color: ${restBorder} !important; }`,
  );
  rules.push(
    `${s} .react-international-phone-dial-code-preview:not(.react-international-phone-dial-code-preview--disabled) { border-color: ${restBorder} !important; }`,
  );

  if (hoverBorder) {
    rules.push(`${s} .ant-input:hover:not(:disabled):not([disabled]) { border-color: ${hoverBorder} !important; }`);
    rules.push(`${s} .ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):hover { border-color: ${hoverBorder} !important; }`);
    rules.push(
      `${s} .ant-select:not(.ant-select-disabled):not(.ant-select-customize-input):hover .ant-select-selector { border-color: ${hoverBorder} !important; }`,
    );
    rules.push(`${s} .ant-picker:hover:not(.ant-picker-disabled) { border-color: ${hoverBorder} !important; }`);
    rules.push(
      `${s} .react-international-phone-input-container .react-international-phone-input:hover:not(:disabled):not([disabled]) { border-color: ${hoverBorder} !important; }`,
    );
    rules.push(
      `${s} .react-international-phone-country-selector-button:not(.react-international-phone-country-selector-button--disabled):hover { border-color: ${hoverBorder} !important; }`,
    );
    rules.push(
      `${s} .react-international-phone-dial-code-preview:not(.react-international-phone-dial-code-preview--disabled):hover { border-color: ${hoverBorder} !important; }`,
    );
  }

  if (focusBorder && focusRgb != null) {
    rules.push(`${s} .ant-input:focus, ${s} .ant-input-focused { border-color: ${focusBorder} !important; box-shadow: ${shadow} !important; }`);
    rules.push(
      `${s} .ant-input-affix-wrapper:focus, ${s} .ant-input-affix-wrapper-focused { border-color: ${focusBorder} !important; box-shadow: ${shadow} !important; }`,
    );
    rules.push(
      `${s} .ant-select-focused:not(.ant-select-disabled).ant-select:not(.ant-select-customize-input) .ant-select-selector { border-color: ${focusBorder} !important; box-shadow: ${shadow} !important; }`,
    );
    rules.push(`${s} .ant-picker-focused { border-color: ${focusBorder} !important; box-shadow: ${shadow} !important; }`);
    rules.push(
      `${s} .react-international-phone-input-container .react-international-phone-input:focus { border-color: ${focusBorder} !important; box-shadow: ${shadow} !important; }`,
    );
    rules.push(
      `${s} .react-international-phone-input-container:focus-within .react-international-phone-country-selector-button:not(.react-international-phone-country-selector-button--disabled) { border-color: ${focusBorder} !important; }`,
    );
    rules.push(
      `${s} .react-international-phone-input-container:focus-within .react-international-phone-dial-code-preview:not(.react-international-phone-dial-code-preview--disabled) { border-color: ${focusBorder} !important; }`,
    );
  }

  return rules.join("\n");
}

/**
 * antd `<Button type="link">` (e.g. "Copy identification data") renders `.ant-btn-link` with default blue (#1890ff).
 * Use checkout_styles.link for rest / hover / focus / active.
 */
export function checkoutLinkButtonOverrideCss(scopeSelector, customStyle) {
  const link = customStyle?.link;
  if (!link || typeof link !== "object") return "";

  const rest = link.linkColor?.trim();
  const hover = link.hoverLinkColor?.trim();
  if (!rest && !hover) return "";

  const color = rest || hover;
  const hoverColor = hover || rest;
  const s = scopeSelector;

  return [
    `${s} .ant-btn-link:not(:disabled):not([disabled]) { color: ${color} !important; }`,
    `${s} .ant-btn-link:not(:disabled):not([disabled]):hover, ${s} .ant-btn-link:not(:disabled):not([disabled]):focus { color: ${hoverColor} !important; }`,
    `${s} .ant-btn-link:not(:disabled):not([disabled]):active { color: ${hoverColor} !important; }`,
  ].join("\n");
}

/**
 * Generic primary `<Button type="primary">` fill themed to checkout_styles.primary
 * (fill / border / text + hover). Use for confirm buttons in modals/drawers so the
 * solid action button follows the producer palette instead of antd blue / hardcoded green.
 */
export function checkoutPrimaryButtonOverrideCss(scopeSelector, customStyle) {
  const p = customStyle?.primary;
  if (!p || typeof p !== "object") return "";

  const bg = p.backgroundColor?.trim();
  const bd = p.borderColor?.trim();
  const color = p.color?.trim();
  if (!bg && !bd && !color) return "";

  const hoverBg = p.hoverBgColor?.trim() || bg;
  const hoverBd = p.hoverBorderColor?.trim() || bd || bg;
  const hoverColor = p.hoverColor?.trim() || color;

  const s = scopeSelector;
  const btn = `${s} .ant-btn-primary:not(:disabled):not([disabled])`;
  const rules = [];

  const restDecl = [bg && `background-color: ${bg}`, (bd || bg) && `border-color: ${bd || bg}`, color && `color: ${color}`]
    .filter(Boolean)
    .map((d) => `${d} !important`)
    .join("; ");
  rules.push(`${btn} { ${restDecl}; }`);

  const hoverDecl = [hoverBg && `background-color: ${hoverBg}`, hoverBd && `border-color: ${hoverBd}`, hoverColor && `color: ${hoverColor}`]
    .filter(Boolean)
    .map((d) => `${d} !important`)
    .join("; ");
  rules.push(`${btn}:hover, ${btn}:focus { ${hoverDecl}; }`);

  return rules.join("\n");
}

/**
 * Input.Search with `enterButton` (Tax ID lookup) renders a primary button `.ant-input-search-button.ant-btn-primary`
 * with antd default blue. Map fill / border / text + hover to checkout_styles.primary.
 */
export function checkoutSearchButtonOverrideCss(scopeSelector, customStyle) {
  const p = customStyle?.primary;
  if (!p || typeof p !== "object") return "";

  const bg = p.backgroundColor?.trim();
  const bd = p.borderColor?.trim();
  const color = p.color?.trim();
  if (!bg && !bd && !color) return "";

  const hoverBg = p.hoverBgColor?.trim() || bg;
  const hoverBd = p.hoverBorderColor?.trim() || bd || bg;
  const hoverColor = p.hoverColor?.trim() || color;

  const s = scopeSelector;
  const btn = `${s} .ant-input-search .ant-input-search-button.ant-btn-primary`;
  const rules = [];

  const restDecl = [bg && `background-color: ${bg}`, (bd || bg) && `border-color: ${bd || bg}`, color && `color: ${color}`]
    .filter(Boolean)
    .map((d) => `${d} !important`)
    .join("; ");
  rules.push(`${btn} { ${restDecl}; }`);

  const hoverDecl = [hoverBg && `background-color: ${hoverBg}`, hoverBd && `border-color: ${hoverBd}`, hoverColor && `color: ${hoverColor}`]
    .filter(Boolean)
    .map((d) => `${d} !important`)
    .join("; ");
  rules.push(`${btn}:hover, ${btn}:focus { ${hoverDecl}; }`);

  return rules.join("\n");
}

/**
 * Country picker trigger (`.country-mobile-trigger`) is a default antd `<Button>`; on hover/focus antd paints border + text
 * with @primary-color (#1890ff). Map hover / focus / active to checkout_styles.primary accent.
 */
export function checkoutCountryTriggerOverrideCss(scopeSelector, customStyle) {
  const p = customStyle?.primary;
  if (!p || typeof p !== "object") return "";

  const accent = p.backgroundColor?.trim() || p.borderColor?.trim();
  if (!accent) return "";

  const s = scopeSelector;
  return [
    `${s} .country-mobile-trigger:not(:disabled):not([disabled]):hover, ${s} .country-mobile-trigger:not(:disabled):not([disabled]):focus { color: ${accent} !important; border-color: ${accent} !important; }`,
    `${s} .country-mobile-trigger:not(:disabled):not([disabled]):active { color: ${accent} !important; border-color: ${accent} !important; }`,
  ].join("\n");
}

/**
 * Country picker Modal ("Select country") renders via portal OUTSIDE `.main-container-card`, so the global theme CSS
 * never reaches it. Scope this to the Modal's own className. Covers the search `.ant-input` border (input palette)
 * and the default `<Button>` ("Close") hover/focus accent (primary palette).
 * @param {string} scopeSelector - the Modal className, e.g. `.country-mobile-modal`
 */
export function checkoutModalControlsOverrideCss(scopeSelector, customStyle) {
  const rules = [];

  const inputCss = checkoutFormControlsOverrideCss(scopeSelector, customStyle);
  if (inputCss) rules.push(inputCss);

  const p = customStyle?.primary;
  const accent = typeof p === "object" ? p?.backgroundColor?.trim() || p?.borderColor?.trim() : null;
  if (accent) {
    const s = scopeSelector;
    const btn = `${s} .ant-btn:not(.ant-btn-primary):not(.ant-btn-link):not(:disabled):not([disabled])`;
    rules.push(`${btn}:hover, ${btn}:focus { color: ${accent} !important; border-color: ${accent} !important; }`);
    rules.push(`${btn}:active { color: ${accent} !important; border-color: ${accent} !important; }`);
  }

  return rules.join("\n");
}

/**
 * Typography.Paragraph ellipsis "Ver mais" / expand is an `<a.ant-typography-expand>` with antd default blue (#1890ff).
 * Use checkout_styles.link for rest / hover / active.
 */
export function checkoutTypographyExpandLinkCss(scopeSelector, customStyle) {
  const link = customStyle?.link;
  if (!link || typeof link !== "object") return "";

  const rest = link.linkColor?.trim();
  const hover = link.hoverLinkColor?.trim();
  if (!rest && !hover) return "";

  const color = rest || hover;
  const hoverColor = hover || rest;
  const s = scopeSelector;

  return [
    `${s} .ant-typography-expand { color: ${color} !important; }`,
    `${s} .ant-typography-expand:hover, ${s} .ant-typography-expand:focus { color: ${hoverColor} !important; }`,
    `${s} .ant-typography-expand:active { color: ${hoverColor} !important; }`,
  ].join("\n");
}
